import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listSpaces,
  getSpace,
  listIssues,
  listStatuses,
  listSprints,
  createSprint,
  updateSprint,
  deleteSprint,
  createSpace,
  createIssue,
  updateIssue,
  deleteIssue,
  getIssue,
  listIssueComments,
  addIssueComment,
  listIssueWatchers,
  addIssueWatcher,
  removeIssueWatcher,
  listIssueWorklog,
  addIssueWorklog,
  listIssueHistory,
  updateIssueTimeTracking,
} from './api'
import type { Issue, SpaceDetail } from './api'

// ── Spaces ─────────────────────────────────────────────────────────────────

export function useSpaces() {
  return useQuery({
    queryKey: ['spaces', 'list'],
    queryFn: listSpaces,
    staleTime: 1000 * 60 * 2,
  })
}

export function useSpace(spaceId: string | null) {
  return useQuery({
    queryKey: ['spaces', 'detail', spaceId],
    queryFn: () => getSpace(spaceId!),
    enabled: !!spaceId,
    staleTime: 1000 * 60 * 2,
  })
}

export function useCreateSpace() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { orgId: string; name: string; key: string; type?: string; description?: string; color?: string }) => {
      return createSpace(payload.orgId, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spaces', 'list'] })
    },
  })
}

// ── Issues ─────────────────────────────────────────────────────────────────

export function useIssues(spaceId: string | null, opts?: { onlyMine?: boolean }) {
  const onlyMine = opts?.onlyMine ?? false
  const queryKey = ['spaces', 'issues', spaceId, onlyMine ? 'mine' : 'all']
  return useQuery({
    queryKey,
    queryFn: () => listIssues(spaceId!, { onlyMine }),
    enabled: !!spaceId,
    staleTime: 0,
  })
}

export function useStatuses(spaceId: string | null) {
  return useQuery({
    queryKey: ['spaces', 'statuses', spaceId],
    queryFn: () => listStatuses(spaceId!),
    enabled: !!spaceId,
    staleTime: 1000 * 60 * 5,
  })
}

export function useSprints(spaceId: string | null) {
  return useQuery({
    queryKey: ['spaces', 'sprints', spaceId],
    queryFn: () => listSprints(spaceId!),
    enabled: !!spaceId,
    staleTime: 1000 * 60,
  })
}

export function useIssueMutations(spaceId: string | null) {
  const queryClient = useQueryClient()

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['spaces', 'issues', spaceId] })
  }

  const create = useMutation({
    mutationFn: (payload: Partial<Issue> & { title: string }) => createIssue(spaceId!, payload),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<Issue> }) => updateIssue(id, payload, spaceId ?? undefined),
    onMutate: async ({ id, payload }) => {
      const key = ['spaces', 'issues', spaceId]
      await queryClient.cancelQueries({ queryKey: key })
      const prev = queryClient.getQueryData<Issue[]>(key)
      if (prev) {
        queryClient.setQueryData<Issue[]>(key, prev.map((i) => (i.id === id ? { ...i, ...payload } : i)))
      }
      return { prev }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['spaces', 'issues', spaceId], ctx.prev)
    },
    onSuccess: () => invalidate(),
  })

  const remove = useMutation({
    mutationFn: (id: string) => deleteIssue(id),
    onMutate: async (id: string) => {
      const key = ['spaces', 'issues', spaceId]
      await queryClient.cancelQueries({ queryKey: key })
      const prev = queryClient.getQueryData<Issue[]>(key)
      if (prev) {
        queryClient.setQueryData<Issue[]>(key, prev.filter((i) => i.id !== id))
      }
      return { prev }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['spaces', 'issues', spaceId], ctx.prev)
    },
    onSuccess: invalidate,
  })

  return { create, update, remove }
}

export function useSprintMutations(spaceId: string | null) {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['spaces', 'sprints', spaceId] })

  const createSprintMutation = useMutation({
    mutationFn: (payload: { name: string; goal?: string }) => createSprint(spaceId!, payload),
    onSuccess: invalidate,
  })

  const updateSprintMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { status?: 'planning' | 'future' | 'active' | 'completed'; name?: string; goal?: string } }) =>
      updateSprint(id, payload),
    onSuccess: invalidate,
  })

  const deleteSprintMutation = useMutation({
    mutationFn: (id: string) => deleteSprint(id),
    onSuccess: invalidate,
  })

  return {
    createSprint: createSprintMutation,
    updateSprint: updateSprintMutation,
    deleteSprint: deleteSprintMutation,
  }
}

export function useIssueDetail(issueId: string | null) {
  return useQuery({
    queryKey: ['spaces', 'issue-detail', issueId],
    queryFn: () => getIssue(issueId!),
    enabled: !!issueId,
    staleTime: 1000 * 30,
  })
}

export function useIssueComments(issueId: string | null) {
  return useQuery({
    queryKey: ['spaces', 'issue-comments', issueId],
    queryFn: () => listIssueComments(issueId!),
    enabled: !!issueId,
    staleTime: 1000 * 10,
  })
}

export function useAddComment(issueId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (content: string) => addIssueComment(issueId!, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spaces', 'issue-comments', issueId] })
      queryClient.invalidateQueries({ queryKey: ['spaces', 'issue-detail', issueId] })
    },
  })
}

// ── Watchers ─────────────────────────────────────────────────────────────────

export function useIssueWatchers(issueId: string | null) {
  return useQuery({
    queryKey: ['spaces', 'issue-watchers', issueId],
    queryFn: () => listIssueWatchers(issueId!),
    enabled: !!issueId,
    staleTime: 1000 * 30,
  })
}

export function useAddWatcher(issueId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => addIssueWatcher(issueId!, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spaces', 'issue-watchers', issueId] })
    },
  })
}

export function useRemoveWatcher(issueId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (watcherId: string) => removeIssueWatcher(issueId!, watcherId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spaces', 'issue-watchers', issueId] })
    },
  })
}

// ── History ──────────────────────────────────────────────────────────────────

export function useIssueHistory(issueId: string | null) {
  return useQuery({
    queryKey: ['spaces', 'issue-history', issueId],
    queryFn: () => listIssueHistory(issueId!),
    enabled: !!issueId,
    staleTime: 1000 * 30,
  })
}

// ── Worklog ──────────────────────────────────────────────────────────────────

export function useIssueWorklog(issueId: string | null) {
  return useQuery({
    queryKey: ['spaces', 'issue-worklog', issueId],
    queryFn: () => listIssueWorklog(issueId!),
    enabled: !!issueId,
    staleTime: 1000 * 10,
  })
}

export function useAddWorklog(issueId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { hours: number; comment?: string; startedAt?: string }) =>
      addIssueWorklog(issueId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spaces', 'issue-worklog', issueId] })
      queryClient.invalidateQueries({ queryKey: ['spaces', 'issue-detail', issueId] })
    },
  })
}



export function useUpdateTimeTracking(issueId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { timeSpent: number; timeRemaining: number | null }) =>
      updateIssueTimeTracking(issueId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spaces', 'issue-detail', issueId] })
      queryClient.invalidateQueries({ queryKey: ['spaces', 'issues', issueId] })
    },
  })
}
